import { useEffect, useMemo, useState } from "react";

import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import { AccountTreeOutlined, SwapHorizOutlined } from "@mui/icons-material";

import { useRecoilValue } from "recoil";

import { userAtom } from "../../../../atoms/atoms";
import { PROFILE_ACESSES } from "../../../../consts/consts";
import { UserType } from "../../../../types/types";

import TransferVehicleComponent from "./TransferVehicleComponent";
import TransferVehicleCustomerComponent from "./TransferVehicleCustomerComponent";

export type VehicleTransferMode = "department" | "customer";

/** Veículo vindo do atalho da tela de veículos, para já abrir pré-selecionado. */
export type VehicleTransferInitialVehicle = {
    id: number;
    vehiclePlate: string;
    departmentId: number;
    secretaryId?: number;
};

type VehicleTransferComponentProps = {
    initialMode?: VehicleTransferMode;
    initialSourceCustomerId?: number;
    initialVehicle?: VehicleTransferInitialVehicle;
};

/**
 * Aba única de transferência de veículos: entre secretarias/departamentos do mesmo cliente
 * ou, para administradores, entre clientes.
 */
const VehicleTransferComponent = ({ initialMode, initialSourceCustomerId, initialVehicle }: VehicleTransferComponentProps) => {
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    const isAdmin = useMemo(
        () => getCurrentUser.configs.isManager && String(getCurrentUser.accessProfile.id) === PROFILE_ACESSES.ADMIN,
        [getCurrentUser.configs.isManager, getCurrentUser.accessProfile.id]
    );

    const [mode, setMode] = useState<VehicleTransferMode>(
        isAdmin && initialMode === "customer" ? "customer" : "department"
    );

    useEffect(() => {
        if (initialMode) {
            setMode(isAdmin && initialMode === "customer" ? "customer" : "department");
        }
    }, [initialMode, isAdmin]);

    return (
        <Box sx={{ width: "100%" }}>
            {isAdmin && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
                    <Typography sx={{ fontFamily: "Prometo", fontWeight: 700, fontSize: 15, color: "#09414A" }}>
                        Tipo de transferência
                    </Typography>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={mode}
                        onChange={(_, value: VehicleTransferMode | null) => value && setMode(value)}
                        sx={{
                            "& .MuiToggleButton-root": { fontFamily: "Prometo", textTransform: "none", gap: 0.75, px: 1.5 },
                            "& .MuiToggleButton-root.Mui-selected": { backgroundColor: "#09414A", color: "#fff" },
                            "& .MuiToggleButton-root.Mui-selected:hover": { backgroundColor: "#0b5260" },
                        }}
                    >
                        <ToggleButton value="department">
                            <AccountTreeOutlined fontSize="small" />
                            Entre secretarias / departamentos
                        </ToggleButton>
                        <ToggleButton value="customer">
                            <SwapHorizOutlined fontSize="small" />
                            Entre clientes
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            {mode === "customer" && isAdmin
                ? <TransferVehicleCustomerComponent initialSourceCustomerId={initialSourceCustomerId} initialVehicleId={initialVehicle?.id} />
                : <TransferVehicleComponent initialCustomerId={initialSourceCustomerId} initialVehicle={initialVehicle} />}
        </Box>
    );
};

export default VehicleTransferComponent;
